import {
  ASSETS_PATH,
  COMMON_ICON,
  PAYMENT_SUBMIT_BUTTON_LABEL,
} from 'constant/paymentConstants';
import { getEcheckAccountTypeLabel } from 'getters/echeckGetters/echeckGetters';
import { getWalletDetails } from 'getters/walletGetters/walletGetters';
import { parseCreditCardDetails } from 'parsers/creditCardParser/creditCardParser';

/**
 * Maps payment method types to functions that extract the relevant last digits.
 *
 * - CREDIT_CARD: Returns the last four digits from the credit card object.
 * - ECHECK: Returns the eCheck suffix from the eCheck object.
 * - WALLET: Returns the last four digits from the wallet object.
 */
const paymentLastDigitsMap = {
  CREDIT_CARD: ({ creditCard }) => creditCard?.lastFourDigits ?? '',
  ECHECK: ({ echeck }) => echeck?.echeckSuffix ?? '',
  WALLET: ({ wallet }) => wallet?.lastFourDigits ?? '',
};

/**
 * Maps payment method types to functions that format the hidden digits string.
 *
 * - CREDIT_CARD: Formats the last digits as a masked credit card number.
 * - ECHECK: Formats the last digits as a masked eCheck number.
 * - WALLET: Formats the last four digits as a masked wallet number.
 */
const paymentHiddenDigitsMap = {
  CREDIT_CARD: digits => `•••• •••• •••• ${digits}`,
  ECHECK: digits => `•••• ••• ${digits}`,
  WALLET: digits => `•••• •••• •••• ${digits}`,
};

/**
 * Maps payment method types to functions that return the icon asset path.
 *
 * - CREDIT_CARD: Returns the asset path for the credit card type.
 * - ECHECK: Returns the asset path for the common icon.
 * - WALLET: Returns the asset path for the wallet type.
 */
const paymentIconMap = {
  CREDIT_CARD: ({ creditCard }) =>
    ASSETS_PATH.replace('%s', creditCard?.cardType),
  ECHECK: () => ASSETS_PATH.replace('%s', COMMON_ICON),
  WALLET: ({ wallet }) => ASSETS_PATH.replace('%s', wallet?.walletType),
};

/**
 * Maps payment method types to functions that return the formatted payment details.
 *
 * - CREDIT_CARD: Returns the formatted credit card details.
 * - ECHECK: Returns the eCheck account type label.
 * - WALLET: Returns the formatted wallet details.
 */
const paymentDetailsMap = {
  CREDIT_CARD: ({ creditCard }) => parseCreditCardDetails(creditCard),
  ECHECK: ({ echeck }) => getEcheckAccountTypeLabel(echeck),
  WALLET: ({ wallet }) => getWalletDetails(wallet),
};

/**
 * Payment submit button label generators for different payment types.
 * Each function takes relevant data and returns formatted label string.
 */
const paymentSubmitLabelMap = {
  subscription: ({ amount }) =>
    PAYMENT_SUBMIT_BUTTON_LABEL.replace('%s', amount.totalMonthlyBill),
  usage: ({ amount }) =>
    PAYMENT_SUBMIT_BUTTON_LABEL.replace('%s', amount.usageOverage),
  other: ({ other }) => PAYMENT_SUBMIT_BUTTON_LABEL.replace('%s', other),
};

export {
  paymentDetailsMap,
  paymentHiddenDigitsMap,
  paymentIconMap,
  paymentLastDigitsMap,
  paymentSubmitLabelMap,
};
